/**
 * RouteGuard - Centralized permission middleware for authenticated routes
 *
 * Wraps all authenticated routes and checks permissions based on the current pathname.
 * Uses the same two-level check as the sidebar:
 *   1. Module permission (e.g. 'dashboard.view')
 *   2. Submodule permission (e.g. dashboard.agent)
 *
 * Behavior:
 * - Public/unguarded route (null) -> allows access
 * - DENY_ACCESS sentinel -> shows AccessDeniedPage (route without configured permissions)
 * - Loading permissions -> shows spinner
 * - Has both module + submodule permission -> renders children
 * - Missing either permission -> shows AccessDeniedPage
 */

import { ReactNode } from 'react'
import { useLocation, Navigate } from 'react-router-dom'
import { usePermissions } from '@/shared/hooks/usePermissions'
import { useAuth } from '@/shared/contexts/AuthContext'
import { useBrokerCertificationGate } from '@/modules/training/hooks/useBrokerCertificationGate'
import { findPermissionForPath, DENY_ACCESS } from './route-permissions'
import { AccessDeniedPage } from '@/shared/components/AccessDeniedPage'

/** Broker training routes that are always accessible (even uncertified).
 * The full certification journey now lives inside CourseDetailPage at
 * /dashboard/broker/learning, so we no longer whitelist the standalone
 * /certification/* pages here — uncertified brokers who try to reach those
 * URLs (e.g. via a stale bookmark) get redirected to the journey, while
 * certified brokers reach them through the Documentos sidebar (gate inactive). */
const BROKER_TRAINING_PATHS = [
  '/dashboard/broker/learning',
  // Documents signing flow — necessary during certification journey
  // because step 9 (and any future signature step) embeds the
  // EnvelopeSigner. Uncertified brokers must be able to reach these
  // URLs to complete signing tasks.
  '/documents/sign',
]

interface RouteGuardProps {
  children: ReactNode
}

export function RouteGuard({ children }: RouteGuardProps) {
  const location = useLocation()
  const { hasPermission, hasSubmodulePermission, isLoading } = usePermissions()
  const { user: authUser } = useAuth()

  // Broker certification gate — redirect uncertified brokers to learning
  const isBrokerUser = authUser?.role === 'owly_broker'
  const brokerGate = useBrokerCertificationGate(isBrokerUser)

  if (isBrokerUser && brokerGate.isGateActive) {
    const isTrainingRoute = BROKER_TRAINING_PATHS.some(p =>
      location.pathname === p || location.pathname.startsWith(p + '/')
    )
    if (!isTrainingRoute) {
      return <Navigate to="/dashboard/broker/learning" replace />
    }
    // Uncertified brokers ON a training route bypass the permission cascade
    // below — those pages (compliance upload, agreement sign, exam) are gated
    // by the certification journey, not by `dashboard.view`. Without this
    // explicit allow, the cascade would deny access because brokers don't have
    // the `dashboard` module permission.
    return <>{children}</>
  }

  // Certified brokers: bypass cascade permission checks for allowed routes.
  // Access is controlled by backend (get_queryset scoping, IsBrokerUser).
  if (isBrokerUser && !brokerGate.isGateActive) {
    const BROKER_PATHS = ['/dashboard/broker', '/leads', '/quotes', '/availability', '/advisor-commissions'];
    const isAllowed = BROKER_PATHS.some(p => location.pathname === p || location.pathname.startsWith(p + '/'));
    if (isAllowed) return <>{children}</>;
  }

  // Owly super users (platform staff) have access to everything by definition
  // of their role. Backend enforces tenant scoping on queries; we don't need
  // granular submodule checks here — that's for company-level users only.
  const isOwlySuperUser = (
    authUser?.role === 'owly_admin' ||
    authUser?.role === 'owly_super_admin' ||
    authUser?.role === 'platform_admin'
  )
  if (isOwlySuperUser) {
    return <>{children}</>
  }

  const routePermission = findPermissionForPath(location.pathname)

  // No permission mapping for this path (public/unguarded) -> allow access
  if (!routePermission) {
    return <>{children}</>
  }

  // Route has no configured permissions -> deny by default
  if (routePermission === DENY_ACCESS) {
    return (
      <AccessDeniedPage
        message="Esta ruta no tiene permisos configurados. Contacta al equipo de desarrollo."
      />
    )
  }

  // Permissions still loading -> show spinner
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            Verificando permisos...
          </p>
        </div>
      </div>
    )
  }

  // 1. Check module-level permission (e.g. 'dashboard.view')
  if (!hasPermission(routePermission.permission)) {
    const [module, action] = routePermission.permission.split('.')
    return <AccessDeniedPage module={module} action={action} />
  }

  // 2. Check submodule permission (e.g. dashboard.agent) - same as sidebar
  if (routePermission.submodule) {
    const { module, submodule } = routePermission.submodule
    if (!hasSubmodulePermission(module, submodule)) {
      return <AccessDeniedPage module={module} />
    }
  }

  return <>{children}</>
}
